"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { Loader2 } from "lucide-react";
import { teamChatService, type Gif, type MediaKind } from "./TeamChatService";
import { getGifFavorites, toggleGifFavorite } from "./helpers";

export interface GifPickerProps {
  onSelect: (url: string) => void;
  /** "gifs" (opaque, cover-cropped) or "stickers" (transparent, contained). */
  kind?: MediaKind;
  /** Stickers only: post an AI-generated sticker. Hidden when absent. */
  onGenerate?: (file: File) => Promise<void> | void;
}

const PER_PAGE = 24;

const COPY: Record<MediaKind, { searchLabel: string; emptyFavorites: string; noResults: string }> = {
  gifs: {
    searchLabel: "Search GIFs",
    emptyFavorites: "No favorites yet — star a GIF to save it",
    noResults: "No GIFs found",
  },
  stickers: {
    searchLabel: "Search stickers",
    emptyFavorites: "No favorites yet — star a sticker to save it",
    noResults: "No stickers found",
  },
  memes: {
    searchLabel: "Search memes",
    emptyFavorites: "No favorites yet — star a meme to save it",
    noResults: "No memes found",
  },
  clips: {
    searchLabel: "Search clips",
    emptyFavorites: "No favorites yet — star a clip to save it",
    noResults: "No clips found",
  },
};

export function GifPicker({ onSelect, kind = "gifs", onGenerate }: GifPickerProps) {
  const [search, setSearch] = useState("");
  const [gifs, setGifs] = useState<Gif[]>([]);
  const [favorites, setFavorites] = useState<Gif[]>([]);
  const [tab, setTab] = useState<"trending" | "favorites">("trending");
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasNext, setHasNext] = useState(false);
  const [failed, setFailed] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [prompt, setPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState("");
  const suggestRef = useRef<NodeJS.Timeout>(null);
  const debounceRef = useRef<NodeJS.Timeout>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  // Which page each loader is on, and which query is live — refs so the scroll
  // handler reads the current value without being torn down and rebound.
  const pageRef = useRef(1);
  const queryRef = useRef("");

  const copy = COPY[kind];

  const loadFirstPage = useCallback((query: string) => {
    pageRef.current = 1;
    queryRef.current = query;
    setLoading(true);
    const req = query.trim()
      ? teamChatService.searchMedia(kind, query, PER_PAGE, 1)
      : teamChatService.trendingMedia(kind, PER_PAGE, 1);
    req
      .then(page => {
        // A slower earlier request must not overwrite a newer query's results.
        if (queryRef.current !== query) return;
        setGifs(page.gifs);
        setHasNext(page.hasNext);
        setFailed(!page.ok);
        gridRef.current?.scrollTo({ top: 0 });
      })
      .finally(() => {
        if (queryRef.current === query) setLoading(false);
      });
  }, [kind]);

  useEffect(() => {
    setFavorites(getGifFavorites(kind));
    loadFirstPage("");
  }, [kind, loadFirstPage]);

  // Infinite scroll — ask for the NEXT page, not the same one again.
  const handleScroll = useCallback(() => {
    const el = gridRef.current;
    if (!el || loading || loadingMore || !hasNext || tab === "favorites") return;
    if (el.scrollTop + el.clientHeight < el.scrollHeight - 50) return;

    const query = queryRef.current;
    const next = pageRef.current + 1;
    pageRef.current = next;
    setLoadingMore(true);

    const req = query.trim()
      ? teamChatService.searchMedia(kind, query, PER_PAGE, next)
      : teamChatService.trendingMedia(kind, PER_PAGE, next);
    req
      .then(page => {
        if (queryRef.current !== query) return;
        setHasNext(page.hasNext);
        setGifs(prev => {
          const seen = new Set(prev.map(g => g.id));
          return [...prev, ...page.gifs.filter(g => !seen.has(g.id))];
        });
      })
      .finally(() => setLoadingMore(false));
  }, [kind, loading, loadingMore, hasNext, tab]);

  const handleSearch = (q: string) => {
    setSearch(q);
    setTab("trending");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (suggestRef.current) clearTimeout(suggestRef.current);
    if (!q.trim()) {
      setSuggestions([]);
      loadFirstPage("");
      return;
    }
    debounceRef.current = setTimeout(() => loadFirstPage(q), 400);
    // Suggestions run ahead of the search on a shorter fuse: they are cheap,
    // and seeing a real term beats firing full searches at one that has none.
    suggestRef.current = setTimeout(() => {
      teamChatService.mediaSuggestions(q).then(list => {
        if (queryRef.current !== q && search !== q) return;
        setSuggestions(list.filter(t => t.toLowerCase() !== q.trim().toLowerCase()));
      });
    }, 200);
  };

  const handleGenerate = async () => {
    const text = prompt.trim();
    if (!text || !onGenerate) return;
    setGenerating(true);
    setGenError("");
    try {
      const file = await teamChatService.generateSticker(text);
      await onGenerate(file);
      setPrompt("");
    } catch (err: any) {
      setGenError(err?.message || "Couldn't make that one.");
    } finally {
      setGenerating(false);
    }
  };

  const applySuggestion = (term: string) => {
    setSearch(term);
    setSuggestions([]);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    loadFirstPage(term);
  };

  const handleFavorite = (gif: Gif, e: React.MouseEvent) => {
    e.stopPropagation();
    setFavorites([...toggleGifFavorite(gif, kind)]);
  };

  const isFavorite = (id: string) => favorites.some(f => f.id === id);
  const displayGifs = tab === "favorites" ? favorites : gifs;

  return (
    <div className="w-full min-w-[20rem]">
      {/* Tabs */}
      <div className="flex border-b border-zinc-200 dark:border-zinc-700">
        <button
          onClick={() => setTab("trending")}
          className={`flex-1 py-2 text-xs font-semibold transition-colors ${
            tab === "trending" ? "text-blue-600 border-b-2 border-blue-600" : "text-zinc-400 hover:text-zinc-600"
          }`}
        >
          {search ? "Search" : "Trending"}
        </button>
        <button
          onClick={() => setTab("favorites")}
          className={`flex-1 py-2 text-xs font-semibold transition-colors flex items-center justify-center gap-1 ${
            tab === "favorites" ? "text-amber-500 border-b-2 border-amber-500" : "text-zinc-400 hover:text-zinc-600"
          }`}
        >
          Favorites {favorites.length > 0 && `(${favorites.length})`}
        </button>
      </div>
      {/* Search */}
      <div className="p-2 border-b border-zinc-200 dark:border-zinc-700">
        <input
          value={search}
          onChange={e => handleSearch(e.target.value)}
          placeholder={copy.searchLabel}
          aria-label={copy.searchLabel}
          className="w-full bg-zinc-100 dark:bg-zinc-800 rounded-md px-3 py-1.5 text-sm border-none focus:outline-none focus:ring-1 focus:ring-blue-500/50"
          autoFocus
        />
        {suggestions.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {suggestions.slice(0, 4).map(term => (
              <button
                key={term}
                onClick={() => applySuggestion(term)}
                className="truncate rounded-full border border-zinc-200 px-2 py-0.5 text-[11px] text-zinc-500 transition-colors hover:border-zinc-300 hover:text-zinc-700 dark:border-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200"
              >
                {term}
              </button>
            ))}
          </div>
        )}
      </div>
      {/* Make one — stickers only, and only when the caller can post it. */}
      {onGenerate && (
        <div className="border-b border-zinc-200 p-2 dark:border-zinc-700">
          <div className="flex gap-1.5">
            <input
              value={prompt}
              onChange={e => { setPrompt(e.target.value); setGenError(""); }}
              onKeyDown={e => { if (e.key === "Enter") handleGenerate(); }}
              disabled={generating}
              placeholder="Or describe one to make"
              aria-label="Describe a sticker to generate"
              className="flex-1 rounded-md border-none bg-zinc-100 px-3 py-1.5 text-sm outline-none focus:ring-1 focus:ring-blue-500/50 disabled:opacity-60 dark:bg-zinc-800"
            />
            <button
              onClick={handleGenerate}
              disabled={generating || prompt.trim().length < 3}
              className="shrink-0 rounded-md bg-zinc-900 px-3 text-xs font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-40 dark:bg-zinc-100 dark:text-zinc-900"
            >
              {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Make"}
            </button>
          </div>
          {generating && (
            <p className="mt-1.5 text-[11px] text-zinc-400">
              Drawing it — about ten seconds. It posts straight to the channel.
            </p>
          )}
          {genError && <p className="mt-1.5 text-[11px] text-rose-500">{genError}</p>}
        </div>
      )}

      {/* Grid */}
      <div
        ref={gridRef}
        onScroll={handleScroll}
        className={`grid gap-1 p-2 max-h-72 overflow-y-auto ${kind === "stickers" ? "grid-cols-3" : "grid-cols-2"}`}
      >
        {loading && tab === "trending" ? (
          <div className="col-span-full flex justify-center py-4">
            <Loader2 className="w-5 h-5 animate-spin text-zinc-400" />
          </div>
        ) : displayGifs.length === 0 ? (
          <p className="col-span-full text-center text-xs text-zinc-400 py-4">
            {tab === "favorites"
              ? copy.emptyFavorites
              : failed
                ? "Unavailable right now. Try again in a moment."
                : search.trim()
                  ? `Nothing matches "${search.trim()}"`
                  : copy.noResults}
          </p>
        ) : (
          displayGifs.map(gif => (
            <div key={gif.id} className="relative group/gif">
              <button
                onClick={() => onSelect(gif.url)}
                title={gif.title || undefined}
                className="rounded-md overflow-hidden hover:ring-2 hover:ring-blue-500 transition-all w-full"
              >
                {/* Stickers are transparent and memes carry text — contain both, or
                    cropping eats the caption. GIFs can safely fill the tile. */}
                <img
                  src={gif.preview}
                  alt={gif.title || ""}
                  className={
                    kind === "stickers"
                      ? "w-full h-20 object-contain p-1"
                      : kind === "memes"
                        ? "w-full h-24 object-contain"
                        : "w-full h-24 object-cover"
                  }
                  loading="lazy"
                />
              </button>
              {/* Star/favorite button */}
              <button
                onClick={(e) => handleFavorite(gif, e)}
                aria-label={isFavorite(gif.id) ? "Remove from favorites" : "Save to favorites"}
                className={`absolute top-1 right-1 w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                  isFavorite(gif.id)
                    ? "bg-amber-500 text-white opacity-100"
                    : "bg-black/50 text-white opacity-0 group-hover/gif:opacity-100 focus:opacity-100"
                }`}
              >
                <span className="text-xs">{isFavorite(gif.id) ? "★" : "☆"}</span>
              </button>
            </div>
          ))
        )}
      </div>
      {loadingMore && (
        <div className="flex justify-center py-2">
          <Loader2 className="w-4 h-4 animate-spin text-zinc-400" />
        </div>
      )}
      <div className="px-2 py-1 border-t border-zinc-200 dark:border-zinc-700">
        <p className="text-[9px] text-zinc-400 text-right">Powered by KLIPY</p>
      </div>
    </div>
  );
}
